/**
 * Audio → GPU: a 256×16 waveform texture (one row per voice slot). `setWaveform` copies into a CPU staging
 * buffer; `upload` does exactly one texSubImage2D per frame, and only when something changed.
 * R16F (float upload, core WebGL2) with an R8 UNORM fallback if the float upload is rejected.
 */
export const WAVE_WIDTH = 256;
export const WAVE_ROWS = 16;

export class AudioTexture {
  readonly texture: WebGLTexture;
  /** true when the texture is R8 and the shader must decode v*2-1 (define WAVE_UNORM) */
  readonly unorm: boolean;
  private readonly gl: WebGL2RenderingContext;
  private readonly floatData: Float32Array | null;
  private readonly byteData: Uint8Array | null;
  private dirty = false;

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
    const tex = gl.createTexture();
    if (!tex) throw new Error('createTexture failed');
    this.texture = tex;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    // drain stale errors so the probe below is meaningful
    while (gl.getError() !== gl.NO_ERROR) { /* drain */ }
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R16F, WAVE_WIDTH, WAVE_ROWS, 0, gl.RED, gl.FLOAT, null);
    const floatOk = gl.getError() === gl.NO_ERROR;
    if (floatOk) {
      this.unorm = false;
      this.floatData = new Float32Array(WAVE_WIDTH * WAVE_ROWS);
      this.byteData = null;
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, WAVE_WIDTH, WAVE_ROWS, gl.RED, gl.FLOAT, this.floatData);
    } else {
      this.unorm = true;
      this.floatData = null;
      this.byteData = new Uint8Array(WAVE_WIDTH * WAVE_ROWS).fill(128);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, WAVE_WIDTH, WAVE_ROWS, 0, gl.RED, gl.UNSIGNED_BYTE, this.byteData);
    }
    gl.bindTexture(gl.TEXTURE_2D, null);
  }

  /** Copy 256 floats (-1..1) into row `slot`. Shorter arrays are zero-padded; longer ones are resampled. */
  set(slot: number, data: Float32Array): void {
    if (slot < 0 || slot >= WAVE_ROWS) return;
    const off = slot * WAVE_WIDTH;
    const n = data.length;
    for (let i = 0; i < WAVE_WIDTH; i++) {
      const v = n === WAVE_WIDTH ? data[i]! : n === 0 ? 0 : (data[Math.min(n - 1, Math.floor((i * n) / WAVE_WIDTH))] ?? 0);
      const c = v > 1 ? 1 : v < -1 ? -1 : v;
      if (this.floatData) this.floatData[off + i] = c;
      else if (this.byteData) this.byteData[off + i] = Math.round((c * 0.5 + 0.5) * 255);
    }
    this.dirty = true;
  }

  /** Clear a row (voice gone). */
  clear(slot: number): void {
    if (slot < 0 || slot >= WAVE_ROWS) return;
    const off = slot * WAVE_WIDTH;
    if (this.floatData) this.floatData.fill(0, off, off + WAVE_WIDTH);
    else if (this.byteData) this.byteData.fill(128, off, off + WAVE_WIDTH);
    this.dirty = true;
  }

  /** One texSubImage2D per frame at most. */
  upload(): void {
    if (!this.dirty) return;
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    if (this.floatData) gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, WAVE_WIDTH, WAVE_ROWS, gl.RED, gl.FLOAT, this.floatData);
    else if (this.byteData) gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, WAVE_WIDTH, WAVE_ROWS, gl.RED, gl.UNSIGNED_BYTE, this.byteData);
    gl.bindTexture(gl.TEXTURE_2D, null);
    this.dirty = false;
  }

  destroy(): void { this.gl.deleteTexture(this.texture); }
}
